/**
 * meetingList.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import moment from "moment-timezone";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { EditMeetingDialog } from "./editMeetingModal.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { PermissionLevel } from "../models/auth";
//import { Group, GroupType, PROTOCOL_VERSION } from "../models/group";
import { Meeting, MeetingType } from "../models/meeting";

import { groupServiceFactory } from "../services/group.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
import { dbServiceFactory } from "../services/db.service";

export function MeetingListContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const navigate = useNavigate();
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [meetings, setMeetings] = React.useState<Meeting[]>([]);
  const [meetingToEdit, setMeetingToEdit] = React.useState<Meeting | null>(null);
  const [openEditMeetingDialog, setOpenEditMeeting] = React.useState<boolean>(false);

  React.useEffect(function () {
    if (!authContext.membership) {
      navigate("/login");
      return;
    }
    const accountID = authContext.membership.accountID;
    bluebird.all([
      groupServiceFactory().getMyGroups(accountID, "meeting"),
      conferenceServiceFactory().getMyMeetings(accountID)
    ])
      .then(([groups, meetings]) => {
        const meetingMap: { [id: string]: Meeting } = {};
        for (const meeting of meetings) {
          meetingMap[meeting._id] = meeting;
        }
        for (const group of groups) {
          if (meetingMap[group._id]) {
            meetingMap[group._id].group = group
          } else {
            const created = group.created instanceof Date ?
              moment(group.created) : (group.created || moment());
            meetingMap[group._id] = {
              _id: group._id,
              accountID: accountID,
              hostUserIDs: [],
              meetingname: group.groupname,
              meetingtype: MeetingType.roundtable,
              autoAccept: false,
              token: "",
              sipEnabled: false,
              group: group,
              created: created
            }
          }
        }
        const newMeetings = Object.values(meetingMap);
        newMeetings.sort((a, b) => {
          if (a.created.isAfter(b.created)) { return -1; }
          if (a.created.isBefore(b.created)) { return 1; }
          return 0;
        });
        setMeetings(newMeetings);
      }, err => {
        console.error("Error getting meetings:", err);
      });
  }, [authContext.membership]);

  async function deleteMeeting(index: number): Promise<void> {
    try {
      if (!authContext.membership) { return; }
      const meeting = meetings[index];
      console.log("meeting to delete:", meeting);
      await bluebird.delay(2000);

      await dbServiceFactory().deleteGroup(meeting._id);


    } catch (err) {
      console.error("Error deleting meeting:", err);
    }
  }

  async function deleteMeetingFromServer(index: number): Promise<void> {
    try {
      if (!authContext.membership) { return; }
      const meeting = meetings[index];
      await conferenceServiceFactory().deleteMeeting(authContext.membership.accountID, meeting._id);
      const arr = Array.from(meetings);
      arr.splice(index, 1);
      setMeetings(arr);
    } catch (err) {
      console.error("Error deleting meeting:", err);
      setErrMsg((err as Error).message);
    }
  }

  function didChangeMeeting(meeting: Meeting): void {
    const arr = Array.from(meetings);
    const index = arr.findIndex(m => m._id === meeting._id);
    if (index >= 0) {
      arr[index] = meeting;
    } else {
      arr.unshift(meeting);
    }
    setMeetings(arr);
    if (!meetingToEdit || meetingToEdit && meeting._id === meetingToEdit._id) {
      setMeetingToEdit(meeting);
    }
  }

  function startEditAtIndex(index: number) {
    setMeetingToEdit(meetings[index]);
    setOpenEditMeeting(true);
  }

  const listTitle = authContext.membership && authContext.membership.accountname || "My";

  return <div className="main-section-container both-side-margin">
    <div className="main-scroll-container">
      <header className="iris-header meeting-list-header">
        <div className="logo-img" />

      </header>
      <div className="meeting-list-container">
        <h1>{listTitle} Meetings</h1>

        {authContext.can(PermissionLevel.WRITE, "meetings") &&
          <Button variant="contained" onClick={() => setOpenEditMeeting(true)}>Create Meeting</Button>}

        <div className="meeting-list">
          {meetings.map((gm, i) => {
            return <MeetingListItem key={gm._id}
              domain={authContext.domain}
              meeting={gm}
              displayTZ={authContext.tz()}
              onEdit={() => startEditAtIndex(i)}
              onDelete={() => deleteMeeting(i)}
              onServerDelete={() => deleteMeetingFromServer(i)} />;
          })}
        </div>
      </div>
      {openEditMeetingDialog && <EditMeetingDialog
        open={openEditMeetingDialog}
        meeting={meetingToEdit}
        onClose={() => { setOpenEditMeeting(false); setMeetingToEdit(null); console.log("dialog being closed") }}
        didChangeMeeting={didChangeMeeting} />}
    </div>
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
  </div>;
}

interface ListItemProps {
  meeting: Meeting,
  domain: string,
  displayTZ: string,
  onEdit(): void,
  onDelete(): Promise<void>,
  onServerDelete(): Promise<void>
}
export function MeetingListItem(props: ListItemProps): React.ReactElement<any, any> {
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [tooltipText, setTooltipText] = React.useState<string>("Copy Link");

  if (props.displayTZ) {
    props.meeting.created.tz(props.displayTZ);
  }
  async function onDelete(): Promise<void> {
    try {
      setDeleting(true);
      await props.onDelete();
    } finally {
      setDeleting(false);
    }
  }
  async function onServerDelete(): Promise<void> {
    try {
      setDeleting(true);
      await props.onServerDelete();
    } finally {
      setDeleting(false);
    }
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(conferenceServiceFactory().urlForMeeting(props.meeting, props.domain));
      setTooltipText("Copied!");
      setTimeout(() => {
        setTooltipText("Copy Link");
      }, 5000);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  }

  return <div className="meeting-list-item">
    <div>
      <div className="meeting-name">{props.meeting.meetingname}</div>
      <div className="creation-time">{props.meeting.created.format("MMM DD, YYYY hh:mma")}</div>
    </div>

    <div className="right-controls">
      {props.meeting.token && <>
        <Button disableFocusRipple disableRipple disableTouchRipple size="small"
          component={Link} to={`/meeting/${props.meeting._id}/${props.meeting.token}`}>
          Launch
        </Button>
        <IconButton disableFocusRipple disableRipple disableTouchRipple
          color="primary"
          size="small"
          onClick={copyToClipboard}>
          <Tooltip title={tooltipText}>
            <i className="fa-solid fa-link" />
          </Tooltip>
        </IconButton>
      </>}

      <IconButton disableFocusRipple disableRipple disableTouchRipple
        color="primary" size="small"
        onClick={props.onEdit}>
        <Tooltip title="Edit">
          <i className="fa-solid fa-pen-to-square" />
        </Tooltip>
      </IconButton>
      {deleting ? <CircularProgress size={20} /> :
        props.meeting.group ?
          <IconButton disableFocusRipple disableRipple disableTouchRipple size="small"
            color="primary" onClick={onDelete}>
            <Tooltip title="Delete Locally">
              <i className="fa-solid fa-trash-can" />
            </Tooltip>
          </IconButton> :
          <IconButton disableFocusRipple disableRipple disableTouchRipple size="small"
            color="primary" onClick={onServerDelete}>
            <Tooltip title="Delete on Server">
              <i className="fa-solid fa-rectangle-xmark" />
            </Tooltip>
          </IconButton>}
    </div>
  </div>;
}
